import moment from "moment";
import * as React from "react";
import { useTable } from "react-table";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { DateCell } from "src/components/Displays";
import { TextButton } from "src/components/Forms/Base/Buttons";
import NonConnectedDatePicker from "src/components/Forms/Base/Old/NonConnected/NonConnectedDatePicker";
import { StatusLabel } from "src/components/Lists/Base/CompleteList/styles";
import { axiosInstance } from "src/store/base/store/axios";
import { getInvoiceIdPreview } from "src/store/billectaInvoicing";
import {
  billectaStateMap,
  DELIVERY_METHOD_MAPPER_EXCL_PRICES,
  invoiceDetailUrl,
} from "src/store/billectaInvoicing/utils";
import { addToast, TOAST_TYPES } from "src/store/toasts";
import { Field } from "../Table";
import * as SC from "../Table/styles";

const getUrl = ({ creditorId, fromDate, toDate }) =>
  `/accounting/gateways/invoices/closed/${creditorId}/?sortingfield=InvoiceDate&asc=false&from=${fromDate}&to=${toDate}`;

export default function ClosedInvoicesTable({ creditorId }) {
  const dispatch = useDispatch();
  const { push } = useHistory();

  // State management
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fromDate, setFromDate] = React.useState(
    moment().subtract(3, "months").toDate()
  );
  const [toDate, setToDate] = React.useState(moment().toDate());
  const [searchTerm, setSearchTerm] = React.useState("");

  const handleDownloadInvoice = (invoice) => {
    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Hämtar faktura...",
      })
    );
    dispatch(
      getInvoiceIdPreview({
        invoiceId: invoice?.ActionPublicId,
        download: true,
      })
    );
  };

  const handleRowClicked = (row) => {
    push(
      invoiceDetailUrl({
        creditorId,
        invoiceId: row.original.ActionPublicId,
      })
    );
  };

  const columns = React.useMemo(() => {
    const cols = [
      {
        Header: "Status",
        accessor: "Stage",
        Cell: (props) => {
          if (props.row?.original?.IsDisputed) {
            return (
              <StatusLabel state={6}>
                {billectaStateMap[props.value]} (Bestriden)
              </StatusLabel>
            );
          }

          if (props.row?.original?.ActionType === "CreditInvoiceAction") {
            if (props.row?.original?.AttestedDate == null) {
              return (
                <StatusLabel state={"Kreditfaktura"}>
                  Kreditfaktura (Ej attesterad)
                </StatusLabel>
              );
            }
            return (
              <StatusLabel state={"Kreditfaktura"}>Kreditfaktura</StatusLabel>
            );
          }
          return (
            <StatusLabel state={billectaStateMap[props.value]}>
              {billectaStateMap[props.value]}
            </StatusLabel>
          );
        },
      },
      {
        Header: "Fakturanr.",
        accessor: "InvoiceNumber",
        Cell: (props) => {
          return <Field>{props.value}</Field>;
        },
      },

      {
        Header: "Hyresgäst",
        accessor: "DebtorName",
        Cell: (props) => {
          return <Field>{props.value}</Field>;
        },
      },
      {
        Header: "Originalbelopp (SEK)",
        accessor: "total",
        Cell: (props) => {
          return (
            <Field>
              {(props.row.original.InvoicedAmount.Value / 100).toLocaleString(
                "sv"
              )}
            </Field>
          );
        },
      },
      {
        Header: "Kvarvarande originalbelopp (SEK)",
        accessor: "totalLeft",
        Cell: (props) => {
          return (
            <Field>
              {(props.row.original.CurrentAmount.Value / 100).toLocaleString(
                "sv"
              )}
            </Field>
          );
        },
      },
      {
        Header: "Leveransmetod",
        accessor: "DeliveryMethod",
        Cell: (props) => {
          return (
            <Field>{DELIVERY_METHOD_MAPPER_EXCL_PRICES[props.value]}</Field>
          );
        },
      },
      {
        Header: "Attesterad",
        accessor: "AttestedDate",
        Cell: (props) => {
          return <Field>{props.value ? "Ja" : "Nej"}</Field>;
        },
      },
      {
        Header: "Fakturadatum",
        accessor: "InvoiceDate",
        Cell: (props) => {
          return <DateCell date={props.value} />;
        },
      },
      {
        Header: "Förfallodatum",
        accessor: "DueDate",
        Cell: (props) => {
          return <DateCell date={props.value} />;
        },
      },

      {
        Header: "Skapad",
        accessor: "Created",
        Cell: (props) => {
          return <DateCell date={props.value} />;
        },
      },
      {
        Header: "Betalningsinfo",
        id: "paid_relation",
        Cell: ({ row }) => {
          const closedDate = moment(row.original.ClosedDate);
          const dueDate = moment(row.original.DueDate);
          const now = moment();
          const attested = !!row.original.AttestedDate;
          const isCredit = row.original.ActionType === "CreditInvoiceAction";

          if (!attested || isCredit) return <Field> - </Field>;
          // not paid, late
          if (!closedDate.isValid() && now.isAfter(dueDate)) {
            const lateDays = now.diff(dueDate, "days");

            return (
              <StatusLabel state={6}>
                Ej betalad, försenad med {lateDays} dag
                {lateDays > 1 ? "ar" : ""}
              </StatusLabel>
            );
          }

          if (closedDate.isSameOrBefore(dueDate)) {
            return (
              <StatusLabel state={"Slutbetald"}>Betalad i tid</StatusLabel>
            );
          }

          if (closedDate.isAfter(dueDate)) {
            const latePaymentDays = closedDate.diff(dueDate, "days");
            return (
              <StatusLabel state={"Påminnelse"}>
                Betalad {latePaymentDays} dag
                {latePaymentDays > 1 ? "ar" : ""} för sent
              </StatusLabel>
            );
          }

          return <StatusLabel state={9}>Inväntar betalning</StatusLabel>;
        },
      },
    ];

    if (handleDownloadInvoice) {
      cols.push({
        Header: "Ladda ner",
        Cell: (props) => {
          return (
            <Field style={{ textAlign: "center" }}>
              <TextButton
                title="Ladda ner"
                iconType="download"
                iconPlacement="right"
                clicked={() => handleDownloadInvoice(props.cell.row.original)}
              />
            </Field>
          );
        },
      });
    }

    return cols;
  }, []);

  // Filter data based on search term
  const filteredData = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return data.filter((item) => {
      // Search across relevant text fields
      const searchableFields = [
        item.DebtorName,
        item.InvoiceNumber,
        item.OCR,
        item.YourReference,
        item.CreditorName,
        item.ActionPublicId,
      ];

      return searchableFields.some(
        (field) => field && field.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [data, searchTerm]);

  // Data fetching function
  const fetchData = React.useCallback(async () => {
    if (!creditorId) return;

    setLoading(true);
    try {
      const url = getUrl({
        creditorId,
        fromDate: moment(fromDate).format("YYYY-MM-DD"),
        toDate: moment(toDate).format("YYYY-MM-DD"),
      });

      const response = await axiosInstance.get(url);
      let newData = response.data || [];

      // Sort data by InvoiceDate descending (latest first)
      newData = newData.sort((a, b) => {
        const dateA = moment(a.InvoiceDate);
        const dateB = moment(b.InvoiceDate);
        return dateB.diff(dateA); // descending order
      });

      setData(newData);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      dispatch(
        addToast({
          type: TOAST_TYPES.ERROR,
          title: "Fel vid hämtning av fakturor",
          message: error.message,
        })
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [creditorId, fromDate, toDate, dispatch]);

  // Reset and fetch data when dates change
  const handleFromDateChange = (newFromDate) => {
    setFromDate(newFromDate);
  };

  const handleToDateChange = (newToDate) => {
    setToDate(newToDate);
  };

  // Initial data fetch and when dates change
  React.useEffect(() => {
    fetchData();
  }, [creditorId, fromDate, toDate, fetchData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: filteredData,
    });

  // Handle row click
  const handleTableRowClick = (row) => {
    handleRowClicked(row);
  };

  return (
    <>
      {/* CSS for loading spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Date Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 8 }}>Från datum:</div>
          <NonConnectedDatePicker
            inputExtraStyle={{ maxWidth: 160 }}
            id="billecta_date_from"
            value={fromDate}
            onChange={(date) => {
              handleFromDateChange(date);
            }}
            clearable={false}
            noMargin
            maxDate={toDate}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 8 }}>Till datum:</div>
          <NonConnectedDatePicker
            inputExtraStyle={{ maxWidth: 160 }}
            id="billecta_date_to"
            value={toDate}
            onChange={(date) => {
              handleToDateChange(date);
            }}
            clearable={false}
            noMargin
            minDate={fromDate}
            maxDate={moment().toDate()}
          />
        </div>

        {/* Search Field */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 8 }}>Sök:</div>
          <input
            type="text"
            placeholder="Sök på namn, fakturanummer, OCR, referens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              minWidth: "300px",
              fontSize: "14px",
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                marginLeft: "8px",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                background: "#f5f5f5",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Rensa
            </button>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500">
        <p>
          Tips: Avgränsa datumen för att snabba upp hämtning av avier &
          fakturor. Ju kortare intervall desto snabbare hämtning.
        </p>
      </div>

      <SC.TableWrapper>
        {/* Loading indicator for all fetch operations */}
        {loading && (
          <div
            style={{
              padding: "15px 20px",
              backgroundColor: "#e3f2fd",
              borderLeft: "4px solid #2196f3",
              marginBottom: "10px",
              fontSize: "14px",
              color: "#1565c0",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid #bbdefb",
                borderTop: "2px solid #2196f3",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            {data.length === 0
              ? "Laddar fakturor..."
              : "Uppdaterar fakturor..."}
          </div>
        )}

        {/* Search results info */}
        {searchTerm && !loading && (
          <div
            style={{
              padding: "10px 20px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #dee2e6",
              fontSize: "14px",
              color: "#6c757d",
            }}
          >
            Visar {filteredData.length} av {data.length} fakturor som matchar "
            {searchTerm}"
          </div>
        )}

        <SC.InnerTableWrapper style={{ position: "relative" }}>
          {/* Loading overlay for table */}
          {loading && data.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            />
          )}
          <SC.Table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      style={{
                        borderBottom: "2px solid #ddd",
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    onClick={() => handleTableRowClick(row)}
                    style={{
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.parentElement.style.backgroundColor = "#f9f9f9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.parentElement.style.backgroundColor = "")
                    }
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        style={{
                          padding: "12px",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </SC.Table>

          {/* Data summary */}
          {(data.length > 0 || loading) && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "#666",
                borderTop: "1px solid #eee",
              }}
            >
              {loading && data.length === 0
                ? "Laddar fakturor..."
                : loading && data.length > 0
                ? `Uppdaterar... (${data.length} fakturor från ${moment(
                    fromDate
                  ).format("YYYY-MM-DD")} till ${moment(toDate).format(
                    "YYYY-MM-DD"
                  )})`
                : `Visar ${filteredData.length} av ${
                    data.length
                  } fakturor från ${moment(fromDate).format(
                    "YYYY-MM-DD"
                  )} till ${moment(toDate).format("YYYY-MM-DD")}`}
            </div>
          )}
        </SC.InnerTableWrapper>
      </SC.TableWrapper>
    </>
  );
}
